// frontend/src/hooks/useRouteSync.js
//
// Bridge between React-Router URLs and the legacy activeTab state.
// - On URL change  → derives tabId and calls setActiveTab
// - Wraps setActiveTab so callers can still use it but the URL updates too
//
import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routeForPath, pathForTab } from '../routes';

/**
 * @param {string} activeTab        current activeTab value
 * @param {function} setActiveTabRaw the raw React setState setter
 * @returns {{ setActiveTab, navigate }}
 *   setActiveTab – drop-in replacement that also pushes the URL
 *   navigate     – React-Router navigate function (for convenience)
 */
export default function useRouteSync(activeTab, setActiveTabRaw) {
  const location = useLocation();
  const nav = useNavigate();
  // Prevent infinite loops: track which direction triggered the last change
  const suppressSync = useRef(false);

  // URL → activeTab  (runs whenever pathname changes)
  useEffect(() => {
    if (suppressSync.current) {
      suppressSync.current = false;
      return;
    }
    const route = routeForPath(location.pathname);
    if (route && route.tabId !== activeTab && route.tabId !== 'caseDetails' && route.tabId !== 'documentEditor') {
      setActiveTabRaw(route.tabId);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrapped setActiveTab → also pushes URL
  const setActiveTab = useCallback((tabId) => {
    suppressSync.current = true;
    setActiveTabRaw(tabId);
    const path = pathForTab(tabId);
    if (path && path !== location.pathname) {
      nav(path);
    }
  }, [setActiveTabRaw, nav, location.pathname]);

  return { setActiveTab, navigate: nav };
}
